import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { brandsService } from "api/services/brands.service";
import { lessonProductsService } from "api/services/lesson-products.service";
import { sportsService } from "api/services/sports.service";
import Button from "components/UI/Button";
import { Select } from "components/UI/Input";
import RichTextEditor from "components/UI/RichTextEditor";

import type { Brand } from "api/services/brands.service";
import type { Sport } from "api/services/sports.service";
import type { TicketType } from "api/types/lesson-product.types";
import "./ClassRegistration.scss";

// ─── Local types for option builder ──────────────────────────────────────────
interface LocalOptionValue {
  tempId: number;
  name: string;
  price_delta: number;
}

interface LocalOptionGroup {
  tempId: number;
  name: string;
  is_required: boolean;
  allow_multiple: boolean;
  values: LocalOptionValue[];
}

// ─── Constants ────────────────────────────────────────────────────────────────
const TICKET_TYPE_OPTIONS = [
  { value: "NONE", label: "일반 강습권" },
  { value: "SPECIAL_111", label: "ONEELEVEN CLASS" },
  { value: "SPECIAL_11", label: "ELEVEN CLASS" },
  { value: "SPECIAL_1", label: "ONE CLASS" },
  { value: "ONE_DAY", label: "체험권 (1회)" },
  { value: "GENERAL_1T10", label: "특강권 1~10회권" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
const SectionTitle: React.FC<{ title: string; description?: string }> = ({ title, description }) => (
  <div className="cr-section__title">
    <h3>{title}</h3>
    {description && <p>{description}</p>}
  </div>
);

const Field: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <div className="cr-field">
    <div className="cr-field__label">
      {label}
      {required && <span className="cr-field__required">*</span>}
    </div>
    <div className="cr-field__control">
      {children}
      {hint && <p className="cr-field__hint">{hint}</p>}
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const ClassRegistration: React.FC = () => {
  const navigate = useNavigate();
  const imageInputRef = useRef<HTMLInputElement>(null);

  // 스포츠 목록
  const [sports, setSports] = useState<Sport[]>([]);
  // 브랜드 목록
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    sportsService
      .list()
      .then((res) => setSports(res.data.sports))
      .catch(() => {
        /* ignore */
      });
    brandsService
      .list()
      .then((res) => setBrands(res.data))
      .catch(() => {
        /* ignore */
      });
  }, []);

  // 기본 정보
  const [sport, setSport] = useState("");
  const [name, setName] = useState("");
  const [brand, setBrand] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [detailedContent, setDetailedContent] = useState("");

  // 세션 / 유효기간
  const [sessionsCount, setSessionsCount] = useState(1);
  const [validityPeriod, setValidityPeriod] = useState(365);
  const [theoryRequiredBeforeDay, setTheoryRequiredBeforeDay] = useState("");

  // 가격
  const [price, setPrice] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [discountRate, setDiscountRate] = useState(0);

  // 타입 / 상태
  const [ticketType, setTicketType] = useState<TicketType>("NONE");
  const [isActive, setIsActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isRetakeProduct, setIsRetakeProduct] = useState(false);

  // 이미지
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  // 옵션 그룹 (등록 전 로컬 상태)
  const [optionGroups, setOptionGroups] = useState<LocalOptionGroup[]>([]);

  // 제출 상태
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // 할인율 자동 계산
  useEffect(() => {
    const p = Number(price);
    const dp = Number(discountPrice);
    if (p > 0 && dp > 0 && dp < p) {
      setDiscountRate(Math.round(((p - dp) / p) * 100));
    } else {
      setDiscountRate(0);
    }
  }, [price, discountPrice]);

  // ── 이미지 핸들러 ────────────────────────────────────────────────────────────
  // 상세 내용 에디터 이미지 업로드 → 서버 URL 반환
  const uploadDetailImage = async (file: File): Promise<string> => {
    const { data } = await lessonProductsService.uploadImage(file);
    return data.url;
  };

  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const combined = [...images, ...files].slice(0, 10);
    setImages(combined);
    setImagePreviews(combined.map((f) => URL.createObjectURL(f)));
    e.target.value = "";
  };

  const handleRemoveImage = (idx: number) => {
    URL.revokeObjectURL(imagePreviews[idx]);
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── 옵션 핸들러 ──────────────────────────────────────────────────────────────
  const addOptionGroup = () => {
    setOptionGroups((prev) => [
      ...prev,
      {
        tempId: Date.now(),
        name: "",
        is_required: true,
        allow_multiple: false,
        values: [{ tempId: Date.now() + 1, name: "", price_delta: 0 }],
      },
    ]);
  };

  const removeOptionGroup = (tempId: number) => {
    setOptionGroups((prev) => prev.filter((g) => g.tempId !== tempId));
  };

  const updateGroupField = (tempId: number, field: keyof LocalOptionGroup, value: unknown) => {
    setOptionGroups((prev) => prev.map((g) => (g.tempId === tempId ? { ...g, [field]: value } : g)));
  };

  const addValue = (groupTempId: number) => {
    setOptionGroups((prev) => prev.map((g) => (g.tempId === groupTempId ? { ...g, values: [...g.values, { tempId: Date.now(), name: "", price_delta: 0 }] } : g)));
  };

  const removeValue = (groupTempId: number, valueTempId: number) => {
    setOptionGroups((prev) => prev.map((g) => (g.tempId === groupTempId ? { ...g, values: g.values.filter((v) => v.tempId !== valueTempId) } : g)));
  };

  const updateValue = (groupTempId: number, valueTempId: number, field: "name" | "price_delta", val: string | number) => {
    setOptionGroups((prev) =>
      prev.map((g) =>
        g.tempId === groupTempId
          ? {
              ...g,
              values: g.values.map((v) => (v.tempId === valueTempId ? { ...v, [field]: val } : v)),
            }
          : g,
      ),
    );
  };

  // ── 제출 ─────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!sport || isNaN(Number(sport))) {
      setSubmitError("스포츠 ID를 입력해주세요.");
      return;
    }
    if (!name.trim()) {
      setSubmitError("강습명을 입력해주세요.");
      return;
    }
    if (!price || Number(price) <= 0) {
      setSubmitError("정가를 입력해주세요.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      // 1. 수강권 생성
      const res = await lessonProductsService.create({
        sport: Number(sport),
        name: name.trim(),
        brand: brand.trim(),
        sessions_count: Number(sessionsCount) || 1,
        price,
        discount_price: discountPrice || undefined,
        validity_period: Number(validityPeriod) || 365,
        ticket_type: ticketType,
        theory_required_before_day: theoryRequiredBeforeDay ? Number(theoryRequiredBeforeDay) : undefined,
        short_description: shortDescription,
        detailed_content: detailedContent,
        is_active: isActive,
        is_featured: isFeatured,
        is_retake_product: isRetakeProduct,
        images: images.length > 0 ? images : undefined,
        image_thumbnails: images.map((_, i) => i === 0),
        image_orders: images.map((_, i) => i),
      });

      const productId = res.data.id;

      // 2. 옵션 그룹 & 값 생성
      for (const group of optionGroups) {
        if (!group.name.trim()) continue;
        const gRes = await lessonProductsService.createOptionGroup(productId, {
          name: group.name.trim(),
          is_required: group.is_required,
          allow_multiple: group.allow_multiple,
        });
        const groupId = gRes.data.id;
        for (const val of group.values) {
          if (!val.name.trim()) continue;
          await lessonProductsService.createOptionValue(productId, groupId, {
            name: val.name.trim(),
            price_delta: val.price_delta,
          });
        }
      }

      alert("수강권이 등록되었습니다!");
      navigate("/class-list");
    } catch (err) {
      const data = (err as { response?: { data?: { detail?: string } & Record<string, unknown> } })?.response?.data;
      const msg =
        data?.detail ||
        Object.values(data || {})
          .flat()
          .join(", ") ||
        "수강권 등록 중 오류가 발생했습니다.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="class-registration">
      {/* 헤더 */}
      <div className="cr-header">
        <div className="cr-header__left">
          <h1 className="cr-header__title">{name || "새 수강권 등록"}</h1>
          <p className="cr-header__breadcrumb">수강권 관리 › 수강권 등록</p>
        </div>
        <div className="cr-header__actions">
          <Button variant="outline" disabled={submitting} onClick={() => navigate("/class-list")}>
            취소
          </Button>
          <Button variant="primary" disabled={submitting} onClick={handleSubmit}>
            {submitting ? "처리 중..." : "등록하기"}
          </Button>
        </div>
      </div>

      {submitError && <p className="cr-error">{submitError}</p>}

      <div className="cr-body">
        {/* ── 기본 정보 ── */}
        <section className="cr-section">
          <SectionTitle title="기본 정보" />

          <Field label="스포츠 종류" required>
            <Select value={sport} onChange={(e) => setSport(e.target.value)} options={[{ value: "", label: "스포츠를 선택해주세요" }, ...sports.map((s) => ({ value: String(s.id), label: s.name }))]} />
          </Field>

          <Field label="강습명" required hint="최대 100자">
            <div className="cr-input-with-count">
              <input className="cr-input" placeholder="강습명을 입력해주세요" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
              <span className="cr-input-count">{name.length}/100</span>
            </div>
          </Field>

          <Field label="브랜드">
            <Select value={brand} onChange={(e) => setBrand(e.target.value)} options={[{ value: "", label: "브랜드를 선택해주세요" }, ...brands.map((b) => ({ value: b.name, label: b.name }))]} />
          </Field>

          <Field label="짧은 설명" hint="홈 화면 표시용, 최대 100자">
            <div className="cr-input-with-count">
              <input className="cr-input" placeholder="짧은 설명을 입력해주세요" value={shortDescription} maxLength={100} onChange={(e) => setShortDescription(e.target.value)} />
              <span className="cr-input-count">{shortDescription.length}/100</span>
            </div>
          </Field>

          <Field label="상세 내용">
            <RichTextEditor value={detailedContent} onChange={setDetailedContent} uploadImage={uploadDetailImage} placeholder="상세 내용을 입력해주세요" />
          </Field>

          <Field label="이미지" hint="첫 번째 이미지가 썸네일로 사용됩니다. 최대 10장.">
            <div className="cr-image-upload">
              {imagePreviews.map((src, i) => (
                <div key={i} className="cr-image-upload__item cr-image-upload__item--filled">
                  <img src={src} alt={`이미지${i + 1}`} />
                  {i === 0 && <span className="cr-image-upload__thumb-label">썸네일</span>}
                  <button type="button" className="cr-image-upload__remove" onClick={() => handleRemoveImage(i)}>
                    ✕
                  </button>
                </div>
              ))}
              {images.length < 10 && (
                <div className="cr-image-upload__item cr-image-upload__item--add" onClick={() => imageInputRef.current?.click()}>
                  <span>＋</span>
                  <span>이미지 추가</span>
                </div>
              )}
            </div>
            <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleAddImage} />
          </Field>
        </section>

        {/* ── 가격 / 세션 ── */}
        <section className="cr-section">
          <SectionTitle title="가격 / 세션 설정" />

          <div className="cr-grid-3">
            <Field label="세션 수" required>
              <div className="cr-input-suffix">
                <input className="cr-input cr-input--right" type="number" min={1} value={sessionsCount} onChange={(e) => setSessionsCount(Number(e.target.value))} />
                <span className="cr-suffix">회</span>
              </div>
            </Field>
            <Field label="유효기간" required hint="티켓 유효 기간">
              <div className="cr-input-suffix">
                <input className="cr-input cr-input--right" type="number" min="1" value={validityPeriod} onChange={(e) => setValidityPeriod(Number(e.target.value))} />
                <span className="cr-suffix">일</span>
              </div>
            </Field>
            <Field label="이론 필수 Day" hint="해당 Day 전 이론 이수 필요. 비워두면 불필요.">
              <input className="cr-input" type="number" min="1" placeholder="미설정" value={theoryRequiredBeforeDay} onChange={(e) => setTheoryRequiredBeforeDay(e.target.value)} />
            </Field>
          </div>

          <div className="cr-grid-3">
            <Field label="정가" required>
              <div className="cr-input-suffix">
                <input className="cr-input cr-input--right" type="text" placeholder="0" value={price ? Number(price).toLocaleString("ko-KR") : ""} onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))} />
                <span className="cr-suffix">원</span>
              </div>
            </Field>
            <Field label="할인가" hint="없으면 비워두세요">
              <div className="cr-input-suffix">
                <input className="cr-input cr-input--right" type="text" placeholder="0" value={discountPrice ? Number(discountPrice).toLocaleString("ko-KR") : ""} onChange={(e) => setDiscountPrice(e.target.value.replace(/[^0-9]/g, ""))} />
                <span className="cr-suffix">원</span>
              </div>
            </Field>
            <Field label="할인율 (자동 계산)">
              <div className="cr-input-suffix">
                <input className="cr-input cr-input--right" type="number" readOnly value={discountRate} />
                <span className="cr-suffix">%</span>
              </div>
            </Field>
          </div>

          <Field label="수강권 타입" required>
            <Select value={ticketType} onChange={(e) => setTicketType(e.target.value as TicketType)} options={TICKET_TYPE_OPTIONS} />
          </Field>
        </section>

        {/* ── 상태 설정 ── */}
        <section className="cr-section">
          <SectionTitle title="상태 설정" />

          <div className="cr-grid-3">
            <Field label="판매 여부" required>
              <Select
                value={String(isActive)}
                onChange={(e) => setIsActive(e.target.value === "true")}
                options={[
                  { value: "true", label: "판매 중" },
                  { value: "false", label: "판매 중지" },
                ]}
              />
            </Field>
            <Field label="추천 상품">
              <Select
                value={String(isFeatured)}
                onChange={(e) => setIsFeatured(e.target.value === "true")}
                options={[
                  { value: "false", label: "아니오" },
                  { value: "true", label: "예" },
                ]}
              />
            </Field>
            <Field label="재수강 상품">
              <Select
                value={String(isRetakeProduct)}
                onChange={(e) => setIsRetakeProduct(e.target.value === "true")}
                options={[
                  { value: "false", label: "아니오" },
                  { value: "true", label: "예" },
                ]}
              />
            </Field>
          </div>
        </section>

        {/* ── 강습 옵션 ── */}
        <section className="cr-section">
          <SectionTitle title="강습 옵션" description="수강권에 선택 옵션(예: 인원수, 레벨)을 추가하세요. 등록 후에도 목록에서 관리할 수 있습니다." />

          <div className="cr-options-container">
            {optionGroups.map((group, gIdx) => (
              <div key={group.tempId} className="cr-option-group">
                <div className="cr-option-group__header">
                  <span className="cr-option-group__index">옵션 {gIdx + 1}</span>
                  <input className="cr-input" placeholder="옵션 그룹명 (예: 인원수)" value={group.name} onChange={(e) => updateGroupField(group.tempId, "name", e.target.value)} style={{ flex: 1, minWidth: 0 }} />
                  <label className="cr-checkbox-label">
                    <input type="checkbox" checked={group.is_required} onChange={(e) => updateGroupField(group.tempId, "is_required", e.target.checked)} />
                    필수
                  </label>
                  <label className="cr-checkbox-label">
                    <input type="checkbox" checked={group.allow_multiple} onChange={(e) => updateGroupField(group.tempId, "allow_multiple", e.target.checked)} />
                    다중선택
                  </label>
                  <button type="button" className="cr-btn-icon-remove" onClick={() => removeOptionGroup(group.tempId)}>
                    ✕
                  </button>
                </div>

                <div className="cr-option-values">
                  {group.values.map((val) => (
                    <div key={val.tempId} className="cr-option-value-row">
                      <input className="cr-input" placeholder="옵션값 (예: 1인)" value={val.name} onChange={(e) => updateValue(group.tempId, val.tempId, "name", e.target.value)} style={{ flex: 1 }} />
                      <div className="cr-input-suffix" style={{ width: 150 }}>
                        <input className="cr-input cr-input--right" type="number" placeholder="0" value={val.price_delta} onChange={(e) => updateValue(group.tempId, val.tempId, "price_delta", Number(e.target.value))} />
                        <span className="cr-suffix">원</span>
                      </div>
                      {group.values.length > 1 && (
                        <button type="button" className="cr-btn-icon-remove" onClick={() => removeValue(group.tempId, val.tempId)}>
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <Button variant="secondary" size="sm" onClick={() => addValue(group.tempId)}>
                    + 값 추가
                  </Button>
                </div>
              </div>
            ))}

            <Button variant="outline" size="sm" onClick={addOptionGroup}>
              + 옵션 그룹 추가
            </Button>
          </div>
        </section>

        {/* 하단 버튼 */}
        <div className="cr-footer">
          <Button variant="outline" disabled={submitting} onClick={() => navigate("/class-list")}>
            취소
          </Button>
          <Button variant="primary" disabled={submitting} onClick={handleSubmit}>
            {submitting ? "처리 중..." : "등록하기"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClassRegistration;
